import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';

interface KpiItem {
  label: string;
  value: string | number;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  unavailable?: boolean;
}

interface KpiStripProps {
  items: KpiItem[];
}

export const KpiStrip: React.FC<KpiStripProps> = ({ items }) => {
  return (
    <Strip>
      {items.map((item, i) => (
        <Kpi key={i} $unavailable={!!item.unavailable}>
          <KpiLabel>{item.label}</KpiLabel>
          <KpiValue>{item.unavailable ? '—' : item.value}</KpiValue>
          {item.delta && !item.unavailable && (
            <KpiDelta $direction={item.delta.direction}>
              {item.delta.direction === 'up'
                ? '↑'
                : item.delta.direction === 'down'
                  ? '↓'
                  : '→'}{' '}
              {item.delta.value}
            </KpiDelta>
          )}
          {item.unavailable && <UnavailableHint>Not available</UnavailableHint>}
        </Kpi>
      ))}
    </Strip>
  );
};

const Strip = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: 24px;

  ${media.md} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Kpi = styled.div<{ $unavailable: boolean }>`
  background: var(--white);
  padding: 16px 18px;

  ${({ $unavailable }) =>
    $unavailable &&
    css`
      opacity: 0.45;
      filter: grayscale(0.6);
    `}

  ${media.md} {
    padding: 20px 22px;
  }
`;

const KpiLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  margin-bottom: 10px;
`;

const KpiValue = styled.div`
  font-family: var(--font-sans-bold);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;

  ${media.md} {
    font-size: 32px;
  }
`;

const KpiDelta = styled.div<{ $direction: 'up' | 'down' | 'flat' }>`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ $direction }) =>
    $direction === 'up'
      ? 'var(--olive)'
      : $direction === 'down'
        ? 'var(--crimson)'
        : 'var(--ink-muted)'};
`;

const UnavailableHint = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 10px;
  color: var(--ink-faint);
  font-style: italic;
`;
